import type { GA4AnalyticsEvent } from '@/domain/shared/types'
import type { CheckoutAnalyticsStep } from '@/scopes/checkout/domain/types/bookingDraft'
import {
  AncillariesAvailableEventProps,
  AncillariesSelectedEventProps,
  CheckoutEventProps,
  CouponCodeEventProps,
  ErrorEventProps,
  GA4ImpressionViewAncillariesEventProps,
  GroupsEventProps,
  ImpressionViewAncillariesItem,
  PariticipantsFormFieldErrorEventProps,
  PreferenceSelectedEventProps,
  RyanairEventProps,
} from '@/scopes/checkout/domain/analytics/types'

/** @deprecated Use {@link createImpressionViewAncillariesEvent} instead. */
export function createAncillariesAvailableEvent({ result }: AncillariesAvailableEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ancillaries',
    description: 'ancillary available',
    element: 'element available',
    result,
    interaction: false,
  }
}

export function createAncillariesClickedEvent({
  result,
  type,
  description,
  element,
  interaction = true,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ancillaries',
    description,
    element,
    result,
    interaction,
    type,
  }
}

export function createCouponCodeEvent({
  result,
}: CouponCodeEventProps): GA4AnalyticsEvent {
  return {
    event: 'discount_code_valid',
    result,
    interaction: true,
  }
}

export function createGenericServiceEvent({
  result,
  type,
  description,
  element,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Generic Service',
    description,
    element,
    result,
    interaction: true,
    type,
  }
}

export function createSkiServiceEvent({
  result,
  type,
  description,
  element,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ski',
    description,
    element,
    result,
    interaction: true,
    type,
  }
}

export function createClothingServiceEvent({
  result,
  type,
  description,
  element,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Clothing',
    description,
    element,
    result,
    interaction: true,
    type,
  }
}

export function createCarEvent({
  result,
  type,
  description,
  element,
  interaction = true,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Car',
    description,
    element,
    result,
    interaction,
    type,
  }
}

export function createEquipmentSelectionEvent({
  result,
  type,
  description,
  element,
  validated = true,
}: AncillariesSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'Equipment Selection',
    description,
    element,
    result,
    validated,
    type,
  }
}

export function createRyanairEvent({
  result,
  type,
  description,
  element,
  validated = true,
}: RyanairEventProps): GA4AnalyticsEvent {
  return {
    event: 'Ryanair',
    description,
    element,
    result,
    validated,
    type,
  }
}

export function createGroupsEvent({
  result,
  type,
  description,
  element,
  validated = true,
}: GroupsEventProps): GA4AnalyticsEvent {
  return {
    event: 'Groups',
    description,
    element,
    result,
    validated,
    type,
  }
}

function getDaysBeforeDeparture(departureDate: string) {
  return Math.floor((new Date(departureDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
}

export function createCheckoutEvent(data: CheckoutEventProps) {
  const {
    step,
    userId,
    accoId,
    accoName,
    accoBrand,
    breadcrumbs,
    accoStars,
    transportType,
    boardType,
    duration,
    rating,
    partyComposition,
    totalGuests,
    departureDate,
    price,
    countryId,
    roomType,
    badges,
  } = data

  const daysBeforeDeparture = getDaysBeforeDeparture(departureDate)

  return {
    userId,
    event: 'eec.checkout',
    ecommerce: {
      checkout: {
        actionField: {
          step,
        },
        products: [
          {
            id: accoId,
            name: accoName,
            brand: accoBrand,
            category: breadcrumbs,
            quantity: totalGuests,
            price,
            dimension2: accoStars,
            dimension3: countryId,
            dimension4: transportType,
            dimension5: partyComposition,
            dimension6: badges,
            dimension7: rating,
            dimension8: duration,
            dimension9: partyComposition,
            dimension10: daysBeforeDeparture,
            dimension11: totalGuests,
            dimension13: departureDate,
            dimension28: roomType,
            dimension38: boardType,
          },
        ],
      },
    },
    ...(mapExtraData(data)),
  }
}

function mapExtraData(data: CheckoutEventProps) {
  const {
    accoId,
    accoBrand,
    partyComposition,
    totalGuests,
    departureDate,
    mandatoryPayment,
    accoName,
    breadcrumbs,
    accoStars,
    transportType,
    boardType,
    duration,
    errorDescription,
    rating,
    price,
    totalStepPrice,
    campaignId,
  } = data

  return {
    accoId,
    accoBrand,
    partyComposition,
    totalGuests,
    departureDate,
    mandatoryPayment,
    accoName,
    accoCategory: breadcrumbs,
    accoStars,
    transport: transportType,
    boardType,
    duration,
    ...(errorDescription && { errorDescription }),
    rating,
    entryPrice: price,
    totalStepPrice,
    campaignId,
  }
}

export function createErrorEvent({ description }: ErrorEventProps): GA4AnalyticsEvent {
  return {
    event: 'Error',
    description: description || 'without error message',
    element: 'page',
    interaction: false,
    type: 'error_message_booking_steps',
    result: 'action_step_open',
    validated: false,
  }
}

export function createPreferenceSelectionEvent({
  result,
  type,
}: PreferenceSelectedEventProps): GA4AnalyticsEvent {
  return {
    event: 'checkout_event',
    description: 'accommodation preferences',
    element: 'checkbox',
    interaction: true,
    result,
    type,
  }
}

export function createParticipantsFormFieldErrorEvent({ description, element, type, interaction }: PariticipantsFormFieldErrorEventProps): GA4AnalyticsEvent {
  return {
    event: 'Error',
    description,
    element,
    interaction: interaction ?? false,
    type,
    result: 'field_validation',
    validated: false,
  }
}

export function createImpressionViewAncillariesEvent(
  items: ImpressionViewAncillariesItem[],
  analytics: CheckoutAnalyticsStep,
): GA4ImpressionViewAncillariesEventProps {
  return {
    event: 'eec.impressionView_ancillaries',
    ecommerce: {
      items,
    },
    ...(analytics && mapExtraData(analytics)),
  } as GA4ImpressionViewAncillariesEventProps
}

export function createSubmitEvent(
  items: ImpressionViewAncillariesItem[],
  analytics: CheckoutAnalyticsStep,
): GA4ImpressionViewAncillariesEventProps {
  return {
    event: 'eec.submit',
    ecommerce: {
      items,
    },
    ...(analytics && mapExtraData(analytics)),
  } as GA4ImpressionViewAncillariesEventProps
}

export function createSelectItemEvent(
  items: ImpressionViewAncillariesItem[],
  analytics?: CheckoutAnalyticsStep,
): GA4ImpressionViewAncillariesEventProps {
  return {
    event: 'select_item',
    ecommerce: {
      items,
    },
    ...(analytics && mapExtraData(analytics)),
  } as GA4ImpressionViewAncillariesEventProps
}

export function createRemoveFromCartEvent(
  items: ImpressionViewAncillariesItem[],
  analytics?: CheckoutAnalyticsStep,
): GA4ImpressionViewAncillariesEventProps {
  return {
    event: 'remove_from_cart',
    ecommerce: {
      items,
    },
    ...(analytics && mapExtraData(analytics)),
  } as GA4ImpressionViewAncillariesEventProps
}
